import type { Lesson } from '../evaluator/types.ts';

/**
 * RF-014 / H-022 — desbloqueio sequencial por pré-requisitos declarados.
 *
 * Uma lição fica disponível quando todos os seus pré-requisitos estão
 * concluídos. Sem isto, o conteúdo declara uma sequência pedagógica que a
 * interface ignora — e o estudante entra numa lição que usa conceitos que
 * ainda não viu, o que é o defeito que o validador de conteúdo passa a vida a
 * tentar impedir.
 */

export type EstadoDaLicao = 'concluida' | 'disponivel' | 'bloqueada';

export interface Situacao {
  estado: EstadoDaLicao;
  /** Títulos das lições que faltam. Vazio quando não está bloqueada. */
  faltam: string[];
}

export function situacaoDaLicao(
  licao: Lesson,
  todas: Lesson[],
  concluidas: ReadonlySet<string>,
  /**
   * Salta a sequência: o modo de demonstração **ou** a conta de acesso total.
   * O valor é decidido num sítio só, `state/desbloqueio.ts`.
   *
   * **Isto não é pagamento.** O que se salta aqui é a sequência pedagógica,
   * nunca uma subscrição — quem tem acesso total não tem plano nenhum. Quando
   * o prévio de `acesso_livre` passar a fechar conteúdo a quem não paga (ADR
   * 0010), essa verificação é outra e não deve reutilizar este parâmetro: quem
   * o ligar aqui transforma uma ferramenta de revisão de conteúdo numa
   * subscrição vitalícia gratuita.
   */
  ignorarPrerequisitos = false,
): Situacao {
  if (concluidas.has(licao.id)) return { estado: 'concluida', faltam: [] };
  if (ignorarPrerequisitos) return { estado: 'disponivel', faltam: [] };

  const porId = new Map(todas.map((l) => [l.id, l]));
  const faltam = licao.prerequisites
    .filter((id) => !concluidas.has(id))
    // Um pré-requisito que não existe no pacote é apanhado pelo compilador.
    // Aqui, se aparecer, mostra-se o identificador em vez de desaparecer em
    // silêncio: uma lição bloqueada sem motivo visível é pior do que um id cru.
    .map((id) => porId.get(id)?.title ?? id);

  return faltam.length > 0 ? { estado: 'bloqueada', faltam } : { estado: 'disponivel', faltam: [] };
}

/**
 * Ordena as lições pela sequência declarada. A ordem de leitura do pacote não
 * é garantia de nada.
 */
export function porOrdem(licoes: Lesson[]): Lesson[] {
  return [...licoes].sort((a, b) => a.order_index - b.order_index);
}
